// Attribution capture for the pre-launch waitlist funnel.
//
// Convention:
//   - Standard UTM params: utm_source, utm_medium, utm_campaign, utm_content, utm_term
//   - Shorter alternative: ?ref=<token>  (e.g. ?ref=twitter-hero-thread-feb09)
//   - Both are supported; explicit UTMs win over `ref` for the same key.
//
// Persistence:
//   - On first landing visit with any tag, we stash the tag map in localStorage
//     with a 30-day TTL. A visitor who clicks a Twitter link → browses → signs
//     up hours or days later still gets attributed to the tweet.
//   - Fresh tags on a return visit overwrite the cache — most-recent-click wins,
//     which matches how paid ad platforms attribute conversions.
//   - `getAttribution()` returns the merged view: URL params take precedence
//     over cache. Missing keys are simply absent.

const STORAGE_KEY = "se_attribution_v1";
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "ref"];

function _readCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.savedAt !== "number") return null;
    if (Date.now() - parsed.savedAt > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed.tags || {};
  } catch {
    return null;
  }
}

function _writeCache(tags) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ savedAt: Date.now(), tags }),
    );
  } catch {
    // Private-mode / disabled storage — silently no-op; the current page still
    // gets attribution via URL params, we just can't persist for later.
  }
}

function _parseUrl(search) {
  if (!search) return {};
  const params = new URLSearchParams(search);
  const out = {};
  for (const key of KEYS) {
    const v = params.get(key);
    if (v && v.trim()) out[key] = v.trim().slice(0, 120);
  }
  return out;
}

/**
 * Capture URL params on landing mount. If any relevant tag is present, update
 * the cache (30-day TTL). No-op if no relevant params exist — we DO NOT wipe
 * an existing cache just because someone returns via a bare URL.
 */
export function captureAttributionFromUrl() {
  if (typeof window === "undefined") return {};
  const fresh = _parseUrl(window.location.search);
  if (Object.keys(fresh).length > 0) {
    _writeCache(fresh);
    return fresh;
  }
  return _readCache() || {};
}

/**
 * Get the current attribution snapshot to attach to a waitlist submission.
 * URL params (this visit) win over cached params (previous visit).
 */
export function getAttribution() {
  if (typeof window === "undefined") return {};
  const url = _parseUrl(window.location.search);
  const cached = _readCache() || {};
  return { ...cached, ...url };
}

/** Test-only helper — clears the persisted cache. */
export function _clearAttributionCache() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
